import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Reusable confirmation modal (Architecture Rule 36 + 56).
 *
 * Replaces sweetalert2 entirely. One modal instance lives at the app root;
 * any component calls `const ok = await confirm({...})` and branches on the
 * boolean — same ergonomics the old `await Swal.fire()` code used, so call
 * sites stay a one-line change.
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: t('users.deleteTitle'),
 *     message: t('users.deleteConfirm', { email }),
 *     confirmLabel: t('users.delete'),
 *     danger: true,
 *   });
 *   if (!ok) return;
 *
 * A11y: role="dialog" + aria-modal, labelled by title, ESC + backdrop cancel,
 * focus moves to the modal on open and restores to the trigger on close,
 * Enter confirms. Ubuntu theme tokens, minimal border radius.
 */

const ConfirmContext = createContext(null);

const EMPTY = {
  title: '',
  message: '',
  confirmLabel: '',
  cancelLabel: '',
  danger: false,
};

export function ConfirmProvider({ children }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(EMPTY);
  const resolverRef = useRef(null);
  const confirmBtnRef = useRef(null);
  const lastFocusedRef = useRef(null);

  const settle = useCallback((result) => {
    setOpen(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(result);
    // Restore focus to whatever opened the dialog.
    const prev = lastFocusedRef.current;
    lastFocusedRef.current = null;
    if (prev && typeof prev.focus === 'function') {
      window.requestAnimationFrame(() => prev.focus());
    }
  }, []);

  const confirm = useCallback((options = {}) => {
    lastFocusedRef.current = document.activeElement;
    setOpts({ ...EMPTY, ...options });
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    confirmBtnRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); settle(false); }
      if (e.key === 'Enter')  { e.preventDefault(); settle(true); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, settle]);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {open && (
        <div
          className="modal-overlay"
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget) settle(false); }}
        >
          <div
            className="modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-modal-title"
            aria-describedby="confirm-modal-message"
          >
            <h2 id="confirm-modal-title" className="modal-title">
              {opts.title || t('common.confirmTitle')}
            </h2>
            <p id="confirm-modal-message" className="modal-message">
              {opts.message}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-ghost"
                onClick={() => settle(false)}
              >
                {opts.cancelLabel || t('common.cancel')}
              </button>
              <button
                ref={confirmBtnRef}
                type="button"
                className={`modal-btn ${opts.danger ? 'modal-btn-danger' : 'modal-btn-primary'}`}
                onClick={() => settle(true)}
              >
                {opts.confirmLabel || t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}
